// Sessions (ClientSpace.dc.html): each session Adam has shown, newest first, with its date, the
// recording's length, Watch and Notes. Watch plays the recording on the page in Vimeo's own player
// (the shared RecordingPlayer, bundled from npm). On the client's own page it picks up where they left
// off and is marked watched at 90%, like every other recording (stage4-for-adam 14): their place is
// kept in hub.client_recording_progress, saved as they watch and never while staff view as them, and
// the row says "Watched" or "Carry on from 18:35". Adam's own view plays from the top and saves
// nothing. Notes opens "Adam's notes" under the row.
//
// Adam's view adds "After session 3, Adam only": the recording's Vimeo link (or its embed code) and
// his notes, shown to the client the moment he saves (client_session_notes.shown_at).
import { useMemo, useState } from 'react'
import { RecordingPlayer, NOT_READY, startFrom } from '../../product/Player.jsx'
import { PlayFill, Tick } from '../../product/Icons.jsx'
import { progressPayload } from '../../product/model.js'
import { COPY } from './copy.js'
import { afterTarget, shownSessions, vimeoLinkFrom, whereLine } from './model.js'
import { saveNotes, saveClientProgress } from './data.js'
import '../../styles/recording.css'

export function SessionsSection({ ctx, heading = true, onlyRecordings = false, empty = null }) {
  const { sessions, notes, staff, open, phone } = ctx
  let list = shownSessions(sessions, notes)
  if (onlyRecordings) list = list.filter(s => s.recordingUrl)
  // the box stays on the session it was saved for, with its line, until the page opens again
  const [stuck, setStuck] = useState(null)
  const target = !(staff && open) ? null
    : (stuck && sessions.find(s => s.id === stuck)) || afterTarget(sessions, notes)
  const [notesOpen, setNotesOpen] = useState(null)
  const [playing, setPlaying] = useState(null)
  if (!list.length && !target) return empty ? <p className="ms-empty">{empty}</p> : null

  return (
    <section className="ms-card" data-part="sessions" aria-label={heading ? undefined : COPY.sessionsH2}
      aria-labelledby={heading ? 'ms-sessions-h' : undefined}>
      {heading && <div className="ms-card-head"><h2 className="ms-h2" id="ms-sessions-h">{COPY.sessionsH2}</h2></div>}
      {list.map(x => {
        const isOpen = notesOpen === x.id
        const isPlaying = playing === x.id
        const where = staff ? null : whereLine(progressOf(ctx, x.id))
        return (
          <div key={x.id} className="ms-session">
            <div className="ms-session-row">
              {x.block && (
                <span className="ms-date" aria-hidden="true">
                  <span className="ms-date-small">{x.block.dow}</span>
                  <span className="ms-date-day">{x.block.day}</span>
                  <span className="ms-date-small">{x.block.mon}</span>
                </span>
              )}
              <span className="ms-session-text">
                <span className="ms-session-title">{x.title}</span>
                {x.date && <span className="sr-only">{x.date}</span>}
                {x.length && <span className="ms-session-length">{x.length}</span>}
                {where && (
                  <span className={where.watched ? 'ms-session-where ms-session-where--watched' : 'ms-session-where'}>
                    {where.watched ? <><Tick size={14} />{COPY.watched}</> : COPY.carryOnFrom(where.at)}
                  </span>
                )}
              </span>
              <span className="ms-session-buttons">
                {x.recordingUrl && (
                  <button type="button" className="ms-btn ms-btn--watch" aria-pressed={isPlaying}
                    aria-controls={`ms-play-${x.id}`} aria-label={`${isPlaying ? COPY.playing : COPY.watch}: ${x.title}`}
                    onClick={() => setPlaying(isPlaying ? null : x.id)}>
                    <PlayFill size={12} />{isPlaying ? COPY.playing : COPY.watch}
                  </button>
                )}
                <button type="button" className="ms-btn-line" aria-expanded={isOpen} aria-controls={`ms-notes-${x.id}`}
                  aria-label={`${isOpen ? COPY.hideNotes : COPY.notes}: ${x.title}`}
                  onClick={() => setNotesOpen(isOpen ? null : x.id)}>
                  {isOpen ? COPY.hideNotes : COPY.notes}
                </button>
              </span>
            </div>
            {isPlaying && (
              <div className="ms-panel ms-panel--player" id={`ms-play-${x.id}`}>
                {x.playable
                  ? <SessionPlayer ctx={ctx} session={x} />
                  : <p className="ms-panel-text" role="status">{NOT_READY}</p>}
              </div>
            )}
            {isOpen && (
              <div className="ms-panel" id={`ms-notes-${x.id}`}>
                <span className="ms-panel-label">{COPY.adamsNotes}</span>
                {x.notes
                  ? x.notes.split(/\n\s*\n/).map((para, i) => <p key={i} className="ms-panel-text">{para}</p>)
                  : <p className="ms-panel-text">{COPY.noNotes}</p>}
              </div>
            )}
          </div>
        )
      })}
      {target && <AfterBox key={target.id} ctx={ctx} session={target} onSaved={() => setStuck(target.id)} />}
    </section>
  )
}

const progressOf = (ctx, sessionId) => (ctx.progress || []).find(p => p.session_id === sessionId) || null

// A session's recording, playing as soon as Watch is pressed: on the client's own page from where they
// left off, with their place saved as they watch (the database marks it watched at the 1-2-1's share);
// in Adam's view, or while staff view as the client, from the top or their place, saving nothing.
function SessionPlayer({ ctx, session }) {
  const { staff, viewingAs, phone, me, patch } = ctx
  const saved = staff ? null : progressOf(ctx, session.id)
  const duration = session.duration || 0
  const canSave = !staff && !viewingAs && Boolean(me?.person?.id)
  // what this play adds to: furthest and seconds watched as they stood when it began
  const base = useMemo(() => ({ furthest: saved?.furthest_seconds || 0, watched: saved?.seconds_watched || 0 }), [session.id])
  const seek = useMemo(() => ({ n: 1, seconds: startFrom({ savedSeconds: saved?.position_seconds || 0, duration }) }), [session.id])

  function onReport(report) {
    if (!canSave) return
    saveClientProgress(me.person.id, session.id, progressPayload(base, report, duration))
      .then(row => { if (row) patch(d => ({ ...d, progress: [...(d.progress || []).filter(p => p.session_id !== session.id), row] })) })
      .catch(() => { /* the next report tries again */ })
  }

  return (
    <RecordingPlayer title={`${ctx.product.name}, ${session.title}`} content={{ vimeo_url: session.recordingUrl, duration_seconds: duration || null }}
      seek={seek} canSave={canSave} onReport={onReport} watchedShare={Number(ctx.product?.watched_share) || 0.9} phone={phone} />
  )
}

// "After session 3, Adam only": the link and the notes, saved and shown to the client at once
function AfterBox({ ctx, session, onSaved }) {
  const { first, patch } = ctx
  const n = session.number
  const [link, setLink] = useState('')
  const [text, setText] = useState('')
  const [msg, setMsg] = useState(null)
  const [busy, setBusy] = useState(false)

  async function save() {
    if (busy) return
    if (!link.trim()) { setMsg({ bad: true, text: COPY.afterNeedLink }); return }
    const url = vimeoLinkFrom(link)
    if (!url) { setMsg({ bad: true, text: COPY.afterBadLink }); return }
    setBusy(true)
    setMsg(null)
    try {
      const row = await saveNotes({ sessionId: session.id, recordingUrl: url, notes: text.trim() })
      onSaved()
      patch(d => ({ ...d, notes: [...d.notes.filter(x => x.session_id !== row.session_id), row] }))
      setLink(url)
      setMsg({ bad: false, text: COPY.afterSaved(first, n) })
    } catch (err) {
      setMsg({ bad: true, text: err?.code === '23514' ? COPY.afterBadLink : COPY.notSaved })
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="ms-after">
      <span className="ms-after-h">{COPY.afterBox(n)}</span>
      <div className="ms-after-fields">
        <input aria-label={COPY.afterLinkAria(n)} value={link} placeholder={COPY.afterLinkPlaceholder} onChange={e => setLink(e.target.value)} />
        <textarea aria-label={COPY.afterNotesAria(n)} value={text} placeholder={COPY.afterNotesPlaceholder(first)}
          onChange={e => setText(e.target.value)} />
      </div>
      <div className="ms-after-foot">
        <button type="button" className="ms-btn ms-btn--small" onClick={save} disabled={busy}>{COPY.afterSave(first)}</button>
        {msg && <span className="ms-box-msg" role={msg.bad ? 'alert' : 'status'}>{msg.text}</span>}
      </div>
    </div>
  )
}
